/**
 * PostSessionPipelineService
 *
 * Orchestrates the post-session flow:
 * 1. Audio recording → AssemblyAI transcription (with speaker diarization)
 * 2. Diarized transcript → GPT-4 clinical note draft
 * 3. Generate provider + client session summaries
 * 4. Store everything in Firestore
 */

import { auth, storage } from '../config/firebase';
import { doc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export interface TranscriptionResult {
  text: string;
  diarizedText: string;
  utterances: Array<{
    id: number;
    speaker: string;
    text: string;
    start: number;
    end: number;
    confidence: number;
  }>;
  duration: number;
  speakerCount: number;
  sentiment: Array<{ text: string; sentiment: string; confidence: number }>;
  highlights: Array<{ text: string; count: number; rank: number }>;
  sessionId: string;
}

export interface ClinicalNoteSuggestion {
  template: string;
  content: string;
  sections: Record<string, string>;
  confidence: number;
  sources: string[];
  generatedAt: string;
  warnings: string[];
}

export interface SessionSummary {
  providerSummary: string;
  clientSummary: string;
  keyTopics: string[];
  interventionsUsed: string[];
  homeworkAssigned: string[];
  nextSessionFocus: string[];
  riskFlags: string[];
}

export interface PostSessionResult {
  transcription: TranscriptionResult | null;
  noteSuggestion: ClinicalNoteSuggestion | null;
  summary: SessionSummary | null;
  sessionRecordingId: string;
}

// Timeout wrapper to prevent indefinite API hangs
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms);
    promise.then(
      (val) => { clearTimeout(timer); resolve(val); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });
}

class PostSessionPipelineService {
  /**
   * Get Firebase auth token for API calls
   */
  private async getAuthToken(): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    return user.getIdToken();
  }

  /**
   * Step 1: Upload audio to Firebase Storage, then transcribe via Deepgram.
   * This avoids Vercel's 4.5MB body limit by sending a download URL instead of raw audio.
   */
  async transcribeAudio(audioBlob: Blob, sessionId: string): Promise<TranscriptionResult> {
    const token = await this.getAuthToken();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Upload audio to Firebase Storage (uses session_recordings path which has storage rules)
    const storagePath = `session_recordings/${user.uid}/${sessionId}.webm`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, audioBlob, { contentType: 'audio/webm' });
    const audioUrl = await getDownloadURL(storageRef);

    console.log('[Pipeline] Audio uploaded to Storage:', { sessionId, size: audioBlob.size, path: storagePath });

    // Send URL to transcription API (no body size limit issues)
    const response = await fetch('/api/transcribe-assemblyai', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ audioUrl, sessionId }),
    });

    if (!response.ok) {
      const errorMsg = await this.safeParseError(response, 'Transcription failed');
      throw new Error(errorMsg);
    }

    return response.json();
  }

  /**
   * Safely parse error responses — handles non-JSON error bodies (HTML error pages, timeouts)
   */
  private async safeParseError(response: Response, fallback: string): Promise<string> {
    try {
      const text = await response.text();
      try {
        const json = JSON.parse(text);
        return json.message || json.error || fallback;
      } catch {
        return `${fallback} (HTTP ${response.status}: ${text.substring(0, 200)})`;
      }
    } catch {
      return `${fallback} (HTTP ${response.status})`;
    }
  }

  /**
   * Step 2: Generate AI clinical note draft from transcript
   */
  async generateNoteDraft(
    transcript: string,
    template: 'SOAP' | 'DAP' | 'BIRP' | 'Progress' | 'Initial',
    patientHistory?: {
      diagnoses?: string[];
      medications?: string[];
      previousNotes?: string[];
    },
    sessionType?: string,
    sessionDuration?: number,
    customInstructions?: string
  ): Promise<ClinicalNoteSuggestion> {
    const token = await this.getAuthToken();

    const response = await fetch('/api/generate-notes', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        transcript,
        template,
        patientHistory,
        sessionType,
        sessionDuration,
        customInstructions: customInstructions || 'The transcript includes speaker labels [Speaker A] and [Speaker B]. Speaker A is the provider/therapist and Speaker B is the patient/client. Use the speaker labels to distinguish clinical observations from patient self-reports.',
      }),
    });

    if (!response.ok) {
      const errorMsg = await this.safeParseError(response, 'Note generation failed');
      throw new Error(errorMsg);
    }

    return response.json();
  }

  /**
   * Step 3: Generate session summaries (provider + client facing)
   */
  async generateSessionSummary(
    diarizedTranscript: string,
    highlights: Array<{ text: string }>,
    sentiment: Array<{ text: string; sentiment: string }>
  ): Promise<SessionSummary> {
    const token = await this.getAuthToken();

    const response = await fetch('/api/generate-notes', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        transcript: diarizedTranscript,
        template: 'SOAP', // Use SOAP as base, we'll parse differently
        customInstructions: `Instead of a clinical note, generate a SESSION SUMMARY with this exact JSON structure:
{
  "content": "",
  "sections": {},
  "confidence": 0.9,
  "sources": [],
  "warnings": [],
  "providerSummary": "2-3 paragraph executive summary for the provider: key clinical observations, progress, concerns, and recommended follow-up",
  "clientSummary": "Patient-friendly 2-3 paragraph summary of session highlights, takeaways, and encouragement. Use warm, supportive language. Do NOT include clinical terminology or diagnoses.",
  "keyTopics": ["Array of main topics discussed"],
  "interventionsUsed": ["Array of therapeutic techniques used (CBT, motivational interviewing, etc.)"],
  "homeworkAssigned": ["Array of homework or between-session tasks discussed"],
  "nextSessionFocus": ["Array of areas to focus on in next session"],
  "riskFlags": ["Array of any safety concerns or risk factors noted. Empty if none."]
}

Key highlights from the session: ${highlights.map(h => h.text).join(', ')}`,
      }),
    });

    if (!response.ok) {
      const errorMsg = await this.safeParseError(response, 'Summary generation failed');
      throw new Error(errorMsg);
    }

    const result = await response.json();

    return {
      providerSummary: result.providerSummary || result.content || '',
      clientSummary: result.clientSummary || '',
      keyTopics: result.keyTopics || [],
      interventionsUsed: result.interventionsUsed || [],
      homeworkAssigned: result.homeworkAssigned || [],
      nextSessionFocus: result.nextSessionFocus || [],
      riskFlags: result.riskFlags || [],
    };
  }

  /**
   * Step 4: Store session recording metadata in Firestore
   */
  async saveSessionRecording(
    sessionId: string,
    appointmentId: string,
    providerId: string,
    patientId: string,
    duration: number,
    transcription: TranscriptionResult | null,
    noteSuggestion: ClinicalNoteSuggestion | null,
    summary: SessionSummary | null
  ): Promise<string> {
    const recordingId = `rec_${sessionId}`;

    await setDoc(doc(db, 'session_recordings', recordingId), {
      sessionId,
      appointmentId,
      providerId,
      patientId,
      duration,
      hasTranscription: !!transcription,
      hasNoteSuggestion: !!noteSuggestion,
      hasSummary: !!summary,
      transcriptionText: transcription?.diarizedText || null,
      utteranceCount: transcription?.utterances?.length || 0,
      speakerCount: transcription?.speakerCount || 0,
      noteSuggestion: noteSuggestion ? {
        template: noteSuggestion.template,
        content: noteSuggestion.content,
        sections: noteSuggestion.sections,
        confidence: noteSuggestion.confidence,
        warnings: noteSuggestion.warnings,
      } : null,
      summary: summary || null,
      status: 'completed',
      createdAt: serverTimestamp() as Timestamp,
    });

    return recordingId;
  }

  /**
   * Replace generic speaker labels (Speaker A, Speaker B) with actual names.
   * Deepgram returns Speaker 0 → A, Speaker 1 → B, etc.
   * Convention: Speaker A = provider, Speaker B = patient/client.
   */
  private labelSpeakers(diarizedText: string, providerName: string, patientName: string): string {
    return diarizedText
      .replace(/\[Speaker A\]/g, `[${providerName}]`)
      .replace(/\[Speaker B\]/g, `[${patientName}]`);
  }

  /**
   * Run the full post-session pipeline
   */
  async runPipeline(
    audioBlob: Blob,
    sessionId: string,
    appointmentId: string,
    providerId: string,
    patientId: string,
    noteTemplate: 'SOAP' | 'DAP' | 'BIRP' | 'Progress' | 'Initial' = 'SOAP',
    patientHistory?: {
      diagnoses?: string[];
      medications?: string[];
      previousNotes?: string[];
    },
    providerName?: string,
    patientName?: string
  ): Promise<PostSessionResult> {
    let transcription: TranscriptionResult | null = null;
    let noteSuggestion: ClinicalNoteSuggestion | null = null;
    let summary: SessionSummary | null = null;

    // Step 1: Transcribe (2 min timeout — Deepgram is synchronous)
    try {
      transcription = await withTimeout(
        this.transcribeAudio(audioBlob, sessionId),
        120000,
        'Transcription'
      );
    } catch (error) {
      console.error('[Pipeline] Transcription failed:', error);
    }

    // Replace generic speaker labels with actual names for AI context
    const labeledTranscript = transcription?.diarizedText
      ? (providerName && patientName
          ? this.labelSpeakers(transcription.diarizedText, providerName, patientName)
          : transcription.diarizedText)
      : null;

    // Step 2 & 3: Generate note draft and summary in parallel (2 min timeout each)
    if (labeledTranscript) {
      const [noteResult, summaryResult] = await Promise.allSettled([
        withTimeout(
          this.generateNoteDraft(
            labeledTranscript,
            noteTemplate,
            patientHistory,
            'Session',
            transcription!.duration,
            providerName && patientName
              ? `The transcript includes speaker labels. [${providerName}] is the provider/therapist and [${patientName}] is the patient/client. Use the speaker labels to distinguish clinical observations from patient self-reports.`
              : undefined
          ),
          120000,
          'Note generation'
        ),
        withTimeout(
          this.generateSessionSummary(
            labeledTranscript,
            transcription!.highlights || [],
            transcription!.sentiment || []
          ),
          120000,
          'Summary generation'
        ),
      ]);

      if (noteResult.status === 'fulfilled') {
        noteSuggestion = noteResult.value;
      } else {
        console.error('[Pipeline] Note generation failed:', noteResult.reason);
      }

      if (summaryResult.status === 'fulfilled') {
        summary = summaryResult.value;
      } else {
        console.error('[Pipeline] Summary generation failed:', summaryResult.reason);
      }
    }

    // Step 4: Save to Firestore — wrapped in try/catch so transcription+notes aren't lost on save failure
    let sessionRecordingId = '';
    try {
      sessionRecordingId = await this.saveSessionRecording(
        sessionId,
        appointmentId,
        providerId,
        patientId,
        transcription?.duration || 0,
        transcription,
        noteSuggestion,
        summary
      );
    } catch (error) {
      console.error('[Pipeline] Firestore save failed — returning data without save:', error);
      sessionRecordingId = `rec_${sessionId}_unsaved`;
    }

    return { transcription, noteSuggestion, summary, sessionRecordingId };
  }
}

export const postSessionPipelineService = new PostSessionPipelineService();
